'use client';

import { Node } from '@xyflow/react';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { IVlanItem, IVlanItemWithVariable } from 'src/context/global/types';
import {
  EDataType,
  EDataSwitchType,
  NodeCategoryModel,
} from 'src/modules/network-configuration/models/node-data';
import { getVariableInfo, validateVariableOption } from 'src/utils/wizard';

import {
  IVariableOptions,
  WizardContextType,
  NodeWithOriginalValue,
} from './types';
import { WizardContext } from './wizard-context';

type Props = {
  children: React.ReactNode;
};

export function WizardProvider({ children }: Props) {
  const [currentVlans, setCurrentVlans] = useState<IVlanItemWithVariable[]>([]);
  const [currentNodes, setCurrentNodes] = useState<NodeWithOriginalValue[]>([]);
  const [currentVariableOptions, setCurrentVariableOptions] = useState<
    Record<string, IVariableOptions>
  >({});
  const [numberOfFilesToExport, setNumberOfFilesToExport] = useState(1);

  const currentSwitches = useMemo(
    () =>
      currentNodes.filter(
        (node) =>
          (node.data?.category as NodeCategoryModel)?.type ===
            EDataType.Switch &&
          (node.data?.category as NodeCategoryModel)?.switchType?.includes(
            EDataSwitchType.MANAGED,
          ),
      ),
    [currentNodes],
  );

  const currentDevices = useMemo(
    () =>
      currentNodes.filter(
        (node) =>
          (node.data?.category as NodeCategoryModel)?.type === EDataType.Device,
      ),
    [currentNodes],
  );

  const variableInfo = useMemo(
    () => getVariableInfo(currentVlans, currentSwitches, currentDevices),
    [currentVlans, currentSwitches, currentDevices],
  );

  // Function to sync with original data when modal opens
  // This preserves variable configurations if they still exist in the new data
  const syncWithOriginalData = useCallback(
    (originalVlans: IVlanItem[], originalNodes: Node[]) => {
      const vlans = originalVlans.map((vlan: IVlanItem) => ({
        ...vlan,
        vlanId: vlan.vlanId.toString(),
        originalValue: JSON.stringify(vlan),
      }));

      const nodes = originalNodes.map((node: Node) => ({
        ...node,
        data: {
          ...node.data,
          category: node.data?.category
            ? { ...(node.data.category as NodeCategoryModel) }
            : undefined,
        },
        originalValue: JSON.stringify(node),
      }));

      // Sync vlans - preserve variables if they exist in original data
      setCurrentVlans((prev) => {
        const syncedVlans = vlans.map((originalVlan) => {
          const existingVlan = prev.find((v) => v.id === originalVlan.id);
          if (
            existingVlan &&
            existingVlan.originalValue === originalVlan.originalValue
          ) {
            return existingVlan;
          }
          return originalVlan;
        });
        return syncedVlans;
      });

      // Sync nodes - preserve variables if they exist in original data
      setCurrentNodes((prev) => {
        const syncedNodes = nodes.map((originalNode) => {
          const existingNode = prev.find((n) => n.id === originalNode.id);
          if (
            existingNode &&
            existingNode.originalValue === originalNode.originalValue
          ) {
            return existingNode;
          }
          return originalNode;
        });
        return syncedNodes;
      });
    },
    [],
  );

  const handleUpdateInterface = useCallback(
    (nodeId: string, index: number, key: string, value: string) => {
      setCurrentNodes((prev) =>
        prev.map((node) => {
          if (node.id === nodeId) {
            const category = node.data?.category as NodeCategoryModel;
            const ipInterfaces = category?.ipInterfaces || [];

            if (ipInterfaces[index]) {
              const updatedIpInterfaces = ipInterfaces.map((ipInterface, i) =>
                i === index ? { ...ipInterface, [key]: value } : ipInterface,
              );

              return {
                ...node,
                data: {
                  ...node.data,
                  category: {
                    ...category,
                    ipInterfaces: updatedIpInterfaces,
                  },
                },
              };
            }
          }
          return node;
        }),
      );
    },
    [setCurrentNodes],
  );

  const handleUpdateNode = useCallback(
    (nodeId: string, key: string, value: string) => {
      setCurrentNodes((prev) =>
        prev.map((node) =>
          node.id === nodeId
            ? {
                ...node,
                data: {
                  ...node.data,
                  category: {
                    ...(node.data?.category as NodeCategoryModel),
                    [key]: value,
                  },
                },
              }
            : node,
        ),
      );
    },
    [setCurrentNodes],
  );

  const handleUpdateVlan = useCallback(
    (vlanId: string, field: 'vlanId' | 'description', value: string) => {
      setCurrentVlans((prev) =>
        prev.map((vlanItem) =>
          vlanItem.id === vlanId ? { ...vlanItem, [field]: value } : vlanItem,
        ),
      );
    },
    [setCurrentVlans],
  );

  const handleUpdateVariableOptions = useCallback(
    (item: string, data: IVariableOptions) => {
      setCurrentVariableOptions((prev) => {
        const updated = {
          ...prev,
          [item]: data,
        };
        return updated;
      });
    },
    [setCurrentVariableOptions],
  );

  // Sync options with variable list: keep existing config, add new variables only.
  // Default range 1..N is already valid — validate immediately so status is not stuck on warning.
  // Use join as a stable dependency (avoid reset when currentNodes only changes by reference).
  // When file count changes, keep range `from` and set `to = from + N - 1` (e.g. 5-10 with N=3 → 5-7).
  const variableItemsKey = variableInfo.items.join(',');
  useEffect(() => {
    const items = variableItemsKey === '' ? [] : variableItemsKey.split(',');

    setCurrentVariableOptions((prev) => {
      const next: Record<string, IVariableOptions> = {};
      items.forEach((item) => {
        if (prev[item]) {
          const preserved = { ...prev[item] };
          if (preserved.type === 'range') {
            preserved.to = preserved.from + numberOfFilesToExport - 1;
          }
          next[item] = validateVariableOption(preserved, numberOfFilesToExport);
        } else {
          next[item] = validateVariableOption(
            {
              type: 'range',
              status: 'warning',
              from: 1,
              to: numberOfFilesToExport,
              list: [],
              errorMessage: '',
            },
            numberOfFilesToExport,
          );
        }
      });
      return next;
    });
  }, [variableItemsKey, numberOfFilesToExport]);

  const handleValidateAllVariableOptions = useCallback(
    (fileCount?: number) => {
      const count = fileCount ?? numberOfFilesToExport;
      setCurrentVariableOptions((prev) => {
        const updated: Record<string, IVariableOptions> = {};
        Object.keys(prev).forEach((item) => {
          const option = { ...prev[item] };
          // Keep `from`, adjust `to` so range length matches file count
          if (option.type === 'range') {
            option.to = option.from + count - 1;
          }
          updated[item] = validateVariableOption(option, count);
        });
        return { ...prev, ...updated };
      });
    },
    [setCurrentVariableOptions, numberOfFilesToExport],
  );

  const hasVariableErrors = useMemo(
    () =>
      Object.values(currentVariableOptions).some(
        (option) => option.status !== 'success' || option.errorMessage !== '',
      ),
    [currentVariableOptions],
  );

  const memoizedValue = useMemo<WizardContextType>(
    () => ({
      currentVlans,
      setCurrentVlans,
      currentNodes,
      setCurrentNodes,
      currentVariableOptions,
      setCurrentVariableOptions,
      numberOfFilesToExport,
      setNumberOfFilesToExport,
      syncWithOriginalData,
      handleUpdateInterface,
      handleUpdateNode,
      handleUpdateVlan,
      handleUpdateVariableOptions,
      variableInfo,
      handleValidateAllVariableOptions,
      hasVariableErrors,
      currentSwitches,
      currentDevices,
    }),
    [
      currentVlans,
      currentNodes,
      currentVariableOptions,
      numberOfFilesToExport,
      syncWithOriginalData,
      handleUpdateInterface,
      handleUpdateNode,
      handleUpdateVlan,
      handleUpdateVariableOptions,
      variableInfo,
      handleValidateAllVariableOptions,
      hasVariableErrors,
      currentSwitches,
      currentDevices,
    ],
  );

  return (
    <WizardContext.Provider value={memoizedValue}>
      {children}
    </WizardContext.Provider>
  );
}
